import {
  DEFAULT_APPROVE_ERROR_MESSAGE,
  DEFAULT_APPROVE_SUCCESS_MESSAGE,
  DEFAULT_REJECT_ERROR_MESSAGE,
  DEFAULT_REJECT_SUCCESS_MESSAGE,
} from './constants/labels'
import type { CancelConfig, CopCaseDetailsProps, ResolutionConfig } from './CopCaseDetails.types'
import { DEFAULT_CASE_ID } from './services/CopCaseDetails.service'

export { parseLabels } from './CopCaseDetails.types'

export const pick = (camel: string | undefined, lower: string | undefined): string | undefined => camel ?? lower

export function parseResolutionConfig(raw?: string): ResolutionConfig {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed as ResolutionConfig : {}
  } catch {
    return {}
  }
}

export function parseCancelConfig(raw?: string): CancelConfig {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed as CancelConfig : {}
  } catch {
    return {}
  }
}

export function resolveCaseId(): string {
  const SAFE_ID = /^[A-Za-z0-9]{15,18}$/
  try {
    const search = new URLSearchParams(window.location.search)
    const fromQuery = search.get('caseId')
    if (fromQuery && SAFE_ID.test(fromQuery)) return fromQuery

    const segments = window.location.pathname.split('/').filter(Boolean)
    const lastSegment = segments.at(-1)?.split('.')[0]
    if (lastSegment && SAFE_ID.test(lastSegment)) return lastSegment
  } catch {
    // eslint-disable-next-line no-empty
  }
  return DEFAULT_CASE_ID
}

export function resolveTabLabels(p: CopCaseDetailsProps) {
  return {
    tab1Label: p.tab1Label ?? p.tab1label,
    tab2Label: p.tab2Label ?? p.tab2label,
    tab3Label: p.tab3Label ?? p.tab3label,
    addCommentLabel: p.addCommentLabel ?? p.addcommentlabel,
    accordionLabels: p.accordionLabels ?? p.accordionlabels,
  }
}

export function resolvePopupLabels(p: CopCaseDetailsProps) {
  return {
    title: pick(p.popupTitle, p.popuptitle),
    description: pick(p.popupDescription, p.popupdescription),
    placeholder: pick(p.popupTextareaPlaceholder, p.popuptextareaplaceholder),
    charLimit: pick(p.popupCharacterLimit, p.popupcharacterlimit),
    fileHelpText: pick(p.popupFileHelpText, p.popupfilehelptext),
    cancelLabel: pick(p.popupCancelLabel, p.popupcancellabel),
    shareLabel: pick(p.popupShareLabel, p.popupsharelabel),
    commentRequiredError: pick(p.popupCommentRequiredError, p.popupcommentrequirederror),
    fileTypeError: pick(p.popupFileTypeError, p.popupfiletypeerror),
    fileSizeErrorMessage: pick(p.popupFileSizeError, p.popupfilesizeerror),
    fileRequiredError: pick(p.popupFileRequiredError, p.popupfilerequirederror),
    submitSuccessMessage: pick(p.popupSubmitSuccessMessage, p.popupsubmitsuccessmessage),
    submitErrorMessage: pick(p.popupSubmitErrorMessage, p.popupsubmiterrormessage),
    selectFileLabel: pick(p.popupSelectFileLabel, p.popupselectfilelabel),
    noFileSelectedLabel: pick(p.popupNoFileSelectedLabel, p.popupnofileselectedlabel),
    cancelConfirmMessage: pick(p.popupCancelConfirmMessage, p.popupcancelconfirmmessage),
    cancelConfirmYesLabel: pick(p.popupCancelConfirmYesLabel, p.popupcancelconfirmyeslabel),
    cancelConfirmNoLabel: pick(p.popupCancelConfirmNoLabel, p.popupcancelconfirmnolabel),
  }
}

export function resolveResolutionBannerLabels(p: CopCaseDetailsProps) {
  return {
    message: pick(p.resolvedBannerMessage, p.resolvedBannermessage),
    rejectLabel: pick(p.resolvedBannerRejectLabel, p.resolvedBannerrejectlabel),
    acceptLabel: pick(p.resolvedBannerAcceptLabel, p.resolvedBanneracceptlabel),
    approveSuccessMessage: pick(p.approveSuccessMessage, p.approvesuccessmessage) ?? DEFAULT_APPROVE_SUCCESS_MESSAGE,
    approveErrorMessage: pick(p.approveErrorMessage, p.approveerrormessage) ?? DEFAULT_APPROVE_ERROR_MESSAGE,
    rejectSuccessMessage: pick(p.rejectSuccessMessage, p.rejectsuccessmessage) ?? DEFAULT_REJECT_SUCCESS_MESSAGE,
    rejectErrorMessage: pick(p.rejectErrorMessage, p.rejecterrormessage) ?? DEFAULT_REJECT_ERROR_MESSAGE,
  }
}

export function resolveRejectModalLabels(p: CopCaseDetailsProps) {
  return {
    title: pick(p.rejectModalTitle, p.rejectmodaltitle),
    helpText: pick(p.rejectModalHelpText, p.rejectmodalhelptext),
    placeholder: pick(p.rejectModalPlaceholder, p.rejectmodalplaceholder),
    charLimit: pick(p.rejectModalCharLimit, p.rejectmodalcharlimit),
    cancelLabel: pick(p.rejectModalCancelLabel, p.rejectmodalcancellabel),
    sendLabel: pick(p.rejectModalSendLabel, p.rejectmodalsendlabel),
  }
}

export function resolveCommentTableLabels(p: CopCaseDetailsProps) {
  return {
    colLastModifiedBy: p.colLastModifiedBy ?? p.collastmodifiedby,
    colDate: p.colDate ?? p.coldate,
    colComments: p.colComments ?? p.colcomments,
    colAttachments: p.colAttachments ?? p.colattachments,
    colViewDownload: p.colViewDownload ?? p.colviewdownload,
    viewLabel: p.viewLabel ?? p.viewlabel,
    downloadLabel: p.downloadLabel ?? p.downloadlabel,
    noCommentsLabel: p.noCommentsLabel ?? p.nocommentslabel,
    numberOfCommentsLabel: p.numberOfCommentsLabel ?? p.numberofcommentslabel,
    itemsPerPageLabel: p.itemsPerPageLabel ?? p.itemsperpagelabel,
    loadingLabel: p.loadingLabel ?? p.loadinglabel,
    fileDownloadErrorLabel: p.fileDownloadErrorLabel ?? p.filedownloaderrorlabel,
  }
}
